// ADR 3.10.26f §3 — the box's panel: one row per present door, each a
// navigating button and a pin toggle, plus "Back to app" inside the console.
//
// `Disclosure`'s ARIA rule: always mounted, `hidden` while closed, so the
// trigger's `aria-controls` never names a missing id. Not a `region`
// landmark — a landmark named after a popover is noise. Anchored to the
// **end** edge (`end-0`), so it opens inward in the RTL locales too.
//
// Rows are `ui/Button`s, not anchors, as the gear was: the box closes, returns
// focus to its trigger and only then navigates.

import { useTranslation } from 'react-i18next'

import { Button } from '../../ui/Button'
import { IconButton } from '../../ui/controls'
import { Icon, type IconName } from '../../ui/icons'
import type { SwitcherEntry, SwitcherId } from './entries'

/** On top of `Button`'s ghost: a full-width, start-aligned row without the link underline. */
const ROW = 'flex-1 justify-start gap-3 px-3 text-start text-ink no-underline'

interface SwitcherPanelProps {
  id: string
  open: boolean
  entries: SwitcherEntry[]
  active: SwitcherId | null
  pins: SwitcherId[]
  inConsole: boolean
  onChoose: (entry: SwitcherEntry) => void
  onBack: () => void
  onTogglePin: (id: SwitcherId) => void
}

function RowButton({ icon, label, current, onClick }: {
  icon: IconName
  label: string
  current: boolean
  onClick: () => void
}) {
  return (
    <Button
      variant="ghost"
      className={current ? `${ROW} font-semibold` : ROW}
      aria-current={current ? 'page' : undefined}
      onClick={onClick}
    >
      <Icon name={icon} />
      <span>{label}</span>
    </Button>
  )
}

export function SwitcherPanel(props: SwitcherPanelProps) {
  const { t } = useTranslation()
  return (
    <div
      id={props.id}
      hidden={!props.open}
      className="absolute end-0 top-full z-10 mt-1 min-w-64 rounded-card border border-boundary bg-surface p-2 shadow-2"
    >
      <ul>
        {props.entries.map((entry) => {
          const name = t(entry.labelKey)
          const pinned = props.pins.includes(entry.id)
          return (
            <li key={entry.id} className="flex items-center gap-1">
              <RowButton
                icon={entry.icon}
                label={name}
                current={props.active === entry.id}
                onClick={() => props.onChoose(entry)}
              />
              <IconButton
                icon={pinned ? 'push_pin' : 'push_pin_outline'}
                label={t(pinned ? 'header_unpin_entry' : 'header_pin_entry', { 0: name })}
                aria-pressed={pinned}
                onClick={() => props.onTogglePin(entry.id)}
              />
            </li>
          )
        })}
        {props.inConsole ? (
          <li className="flex items-center">
            <RowButton icon="home" label={t('admin_switch_to_app')} current={false} onClick={props.onBack} />
          </li>
        ) : null}
      </ul>
    </div>
  )
}
